import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BOOKS = [
  { title: "Bringing Out the Best in People", author: "Aubrey C. Daniels", src: "/assets/books/bringing-out-the-best.jpg" },
  { title: "Performance Management", author: "Aubrey C. Daniels", src: "/assets/books/performance-management.jpg" },
  { title: "The Tipping Point", author: "Malcolm Gladwell", src: "/assets/books/tipping-point.jpg" },
  { title: "The Human Contribution", author: "James Reason", src: "/assets/books/human-contribution.jpg" },
  { title: "Games People Play", author: "Eric Berne", src: "/assets/books/games-people-play.jpg" },
  { title: "Just Culture", author: "Sidney Dekker", src: "/assets/books/just-culture.jpg" },
];

export default function BookCarousel() {
  const [selected, setSelected] = useState(0);
  const [stageWidth, setStageWidth] = useState(1000);
  const stageRef = useRef(null);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => setStageWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const go = useCallback((dir) => {
    setSelected((s) => (s + dir + BOOKS.length) % BOOKS.length);
  }, []);

  const step = Math.min(200, Math.max(110, stageWidth * 0.26));
  const n = BOOKS.length;

  return (
    <section data-testid="book-carousel" aria-label="Books that influenced our approach" className="text-center">
      <div
        ref={stageRef}
        data-testid="book-carousel-stage"
        className="relative mx-auto h-[350px] sm:h-[430px] lg:h-[500px] overflow-hidden"
        style={{ perspective: "1100px" }}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            go(e.key === "ArrowRight" ? 1 : -1);
          }
        }}
      >
        {BOOKS.map((book, i) => {
          let d = i - selected;
          if (d > n / 2) d -= n;
          if (d < -n / 2) d += n;
          const a = Math.abs(d);
          const visible = a <= 2;
          const x = d * step * (a > 1 ? 0.9 : 1);
          const rot = d === 0 ? 0 : d > 0 ? -48 : 48;
          const scale = a === 0 ? 1 : a === 1 ? 0.82 : 0.64;
          return (
            <button
              key={book.title}
              type="button"
              aria-label={`Show ${book.title}`}
              aria-hidden={!visible}
              tabIndex={visible ? 0 : -1}
              data-testid={`book-carousel-cover-${book.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              data-active={a === 0 ? "true" : "false"}
              onClick={() => setSelected(i)}
              className="absolute left-1/2 top-[46%] w-[190px] h-[290px] sm:w-[240px] sm:h-[370px] lg:w-[280px] lg:h-[430px] rounded-lg overflow-hidden cursor-pointer bg-slate-100 border-0 p-0 focus-visible:outline-4 focus-visible:outline-teal focus-visible:outline-offset-4"
              style={{
                transform: `translate(-50%,-50%) translateX(${x}px) translateZ(${-a * 100}px) rotateY(${rot}deg) scale(${scale})`,
                opacity: visible ? (a === 2 ? 0.4 : 1) : 0,
                filter: a > 0 ? "brightness(.88)" : "none",
                zIndex: 20 - a,
                pointerEvents: visible ? "auto" : "none",
                transformOrigin: "center",
                backfaceVisibility: "hidden",
                transition: "transform .65s cubic-bezier(.2,.8,.2,1), opacity .65s, filter .65s",
                boxShadow: a === 0 ? "0 18px 38px #0004" : "0 15px 30px #0002",
              }}
            >
              <img src={book.src} alt={`${book.title} by ${book.author}`} loading={i < 3 ? "eager" : "lazy"} className="h-full w-full object-contain bg-white" />
            </button>
          );
        })}
      </div>

      <p data-testid="book-carousel-caption" aria-live="polite" className="min-h-[38px] font-display text-lg sm:text-xl font-semibold text-teal">
        {BOOKS[selected].title} <span className="font-normal text-slate-500">— {BOOKS[selected].author}</span>
      </p>

      <div className="mt-3 flex items-center justify-center gap-5">
        <button
          type="button"
          aria-label="Previous book"
          data-testid="book-carousel-prev"
          onClick={() => go(-1)}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 bg-white text-teal transition-colors hover:bg-teal-light"
        >
          <ChevronLeft size={22} />
        </button>
        <span data-testid="book-carousel-counter" className="min-w-[65px] font-mono text-sm text-slate-500">
          {selected + 1} / {n}
        </span>
        <button
          type="button"
          aria-label="Next book"
          data-testid="book-carousel-next"
          onClick={() => go(1)}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-300 bg-white text-teal transition-colors hover:bg-teal-light"
        >
          <ChevronRight size={22} />
        </button>
      </div>
      <p className="mt-3 px-4 text-xs text-slate-400">Click a cover or use the arrows to explore the books</p>
    </section>
  );
}
